var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { client, serverErrorMessage } from './client';
import { isAccepted, pollJob } from './jobs';
import { newProgressId } from './progress';
import { checkGameImageSize } from '../utils/img';
/**
 * Upload an image for a specific node
 */
export function uploadNodeImage(data) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Cell art IS the trial card in game, so it must match that slot's fixed size.
            const sizeProblem = yield checkGameImageSize('node-images', data.file);
            if (sizeProblem)
                return { success: false, error: sizeProblem };
            const formData = new FormData();
            formData.append('file', data.file);
            formData.append('levelId', data.levelId.toString());
            formData.append('coord', data.coord);
            formData.append('imageType', data.imageType);
            // override headers for multipart
            const response = yield client.post('/images/node-images', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            return response;
        }
        catch (error) {
            console.error('Error uploading image:', error);
            return {
                success: false,
                error: serverErrorMessage(error, 'Upload failed')
            };
        }
    });
}
/**
 * Generic single-image upload (level background, entity icons, etc.). Returns a
 * relative /uploads/<id> path on success (UploadImageResponse.imageUrl).
 */
export function uploadGenericImage(file, kind) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (kind)
                formData.append('kind', kind);
            return yield client.post('/images/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        catch (error) {
            console.error('Error uploading image:', error);
            return { success: false, error: 'Upload failed' };
        }
    });
}
/**
 * Get all node images for a specific level
 */
export function getNodeImages(levelId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield client.get(`/images/node-images/${levelId}`);
        }
        catch (error) {
            console.error('Error getting node images:', error);
            // Return empty array for now
            return [];
        }
    });
}
/**
 * Get the deleted (archived) node images for a level — for restoring them.
 */
export function getArchivedNodeImages(levelId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield client.get(`/images/node-images/${levelId}?archived=1`);
        }
        catch (error) {
            console.error('Error getting archived node images:', error);
            return [];
        }
    });
}
/**
 * Restore a deleted (archived) node image — un-archive it, no re-upload.
 */
export function restoreNodeImage(imageId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield client.post(`/images/node-images/${imageId}/restore`, {});
        }
        catch (error) {
            console.error('Error restoring node image:', error);
            throw error;
        }
    });
}
/**
 * Get a specific node image by map coord ("A1")
 */
export function getNodeImage(levelId, coord) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield client.get(`/images/node-images/${levelId}/${coord}`);
        }
        catch (error) {
            if (error.response && error.response.status === 404) {
                return null;
            }
            console.error('Error getting node image:', error);
            return null;
        }
    });
}
/**
 * Delete a node image
 */
export function deleteNodeImage(imageId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield client.delete(`/images/node-images/${imageId}`);
        }
        catch (error) {
            console.error('Error deleting node image:', error);
            throw error;
        }
    });
}
/** How many uploaded files nothing references anymore (admin-only preview). */
export function getUploadsGcReport() {
    return client.get('/uploads-gc');
}
/** Delete every unreferenced uploaded file. Admin-only; runs a fresh scan server-side. */
export function runUploadsGc() {
    return client.post('/uploads-gc');
}
/** Which levels carry a background that is not the size the game needs. */
export function getMapNormalizeReport() {
    return client.get('/levels-map-normalize');
}
/** Rewrite every off-size level map at the canonical size (or just one level).
 *  Hundreds of levels means minutes of GridFS + resize work, so the server answers
 *  202 and the result arrives through the progress registry (see api/jobs.ts). */
export function runMapNormalize(levelId) {
    return __awaiter(this, void 0, void 0, function* () {
        const progressId = newProgressId();
        const accepted = yield client.post('/levels-map-normalize', Object.assign({ progressId }, (levelId ? { levelId } : {})));
        if (!isAccepted(accepted))
            return accepted;
        return pollJob(progressId, 'The resize run lost contact with the server. Rescan to see what was done.');
    });
}
/** One-shot migration for a fresh deploy: bring every level in the database in
 *  line with the current rules (canonical map size, unambiguous Empty cells). */
export function runFixAll() {
    return __awaiter(this, void 0, void 0, function* () {
        const progressId = newProgressId();
        const accepted = yield client.post('/fix-all', { progressId });
        if (!isAccepted(accepted))
            return accepted;
        return pollJob(progressId, 'The fix run lost contact with the server. Rescan to see what was done.');
    });
}
